import {
  Component,
  OnInit,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core';
import { ApiDataSource } from 'src/app/utils/api-data-source';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { AppService } from 'src/app/app.service';
import { ApiRequestService } from 'src/app/shared/api-request.service';
import { UtilsService } from 'src/app/shared/utils.service';
import { tap } from 'rxjs/operators';
import { AccountsMergerComponent } from './accounts-merger/accounts-merger.component';
import { MessageEditComponent } from 'src/app/messages/message-edit/message-edit.component';
import { AccountsFilterComponent } from './accounts-filter/accounts-filter.component';
import { AccountModel } from '../../models/account.model';
import { StorageService } from '../../shared/storage.service';
import { RBACService } from '../../shared/rbac.service';
import { Router } from '@angular/router';
import { SentryService } from '../../services/sentry.service';
import { AppcuesService } from '../../services/appcues.service';

@Component({
  selector: 'app-accounts',
  templateUrl: './accounts.component.html',
  styleUrls: ['./accounts.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AccountsComponent implements OnInit {
  displayedColumns: string[] = [
    'select',
    'id',
    'name',
    'subscription',
    'is_internal',
    'enable_sms_notifications',
    'date_created',
    'settings',
    'actions'
  ];

  dataSource = new AccountsDataSource(this.app, this.api);

  // the paginator and sorter
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor(
    private app: AppService,
    private api: ApiRequestService,
    public utils: UtilsService,
    private storage: StorageService,
    private rbac: RBACService,
    private router: Router,
    private sentry: SentryService,
    private appcuesService: AppcuesService
  ) {}

  // Persistent list filters key (localStorage via StorageService).
  private readonly FILTER_STORAGE_KEY = 'admin_accounts_filters';

  ngOnInit() {
    // Restore any persisted filters onto the data source before the first load.
    this.loadFilters();
    this.dataSource.getData(true);
  }

  /**
   * Restore the persisted filter selection (if any) onto the data source.
   */
  private loadFilters() {
    const stored = this.storage.get(this.FILTER_STORAGE_KEY);
    if (!stored) {
      return;
    }
    this.dataSource.subscription = stored.subscription || '';
    this.dataSource.account_type = stored.account_type || '';
    this.dataSource.sms_notifications = stored.sms_notifications || '';
    this.dataSource.associated_users = Array.isArray(stored.associated_users)
      ? stored.associated_users
      : [];
    this.dataSource.include_merged_accounts = !!stored.include_merged_accounts;
    this.dataSource.include_personal_accounts = !!stored.include_personal_accounts;
  }

  /**
   * Persist the current filter selection so it is restored on the next visit.
   */
  private saveFilters() {
    this.storage.set(this.FILTER_STORAGE_KEY, {
      subscription: this.dataSource.subscription,
      account_type: this.dataSource.account_type,
      sms_notifications: this.dataSource.sms_notifications,
      associated_users: this.dataSource.associated_users,
      include_merged_accounts: this.dataSource.include_merged_accounts,
      include_personal_accounts: this.dataSource.include_personal_accounts
    });
  }

  ngAfterViewInit() {
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.order = this.sort.direction;

      // sorting for utc time by parsing original time
      if (this.sort.active === 'date_created_UTC') {
        this.dataSource.order_by = 'date_created';
      } else {
        this.dataSource.order_by = this.sort.active;
      }

      this.dataSource.getData();
    });

    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  onMergeSelected() {
    const selectedAccountIds = this.dataSource.selection.selected;
    if (selectedAccountIds.length < 2) {
      this.utils.showModal(
        'Cannot Merge',
        'You must select at least two accounts to merge.'
      );
      return;
    }

    this.utils.showComponentDialog(
      AccountsMergerComponent,
      {
        selectedAccountIds: selectedAccountIds
      },
      {},
      () => {
        this.dataSource.getData();
        this.dataSource.deselectAll();
      }
    );
  }

  onCombinePreQualifications(account_id: number) {
    this.utils.showModal(
      'Confirmation',
      'Are you sure you want to combine master/slave account ' +
        this.utils.getLangTerm('contractors.singular', 'Contractor') +
        ' Pre-Qualifications?',
      () => {
        this.api
          .makeRequest(
            'put',
            'v2/admin/accounts/combine-slave-pre-qualifications',
            {
              account_id: account_id
            }
          )
          .then((response) => {
            this.utils.showToast('The Pre-Qualifications were combined.');
          });
      }
    );
  }

  composeNewMessage(accountId: number = null) {
    if (this.dataSource.selection.selected.length == 0 && !accountId) {
      this.utils.showToast('You need to select some accounts first.');
      return; // early termination
    }

    this.utils.showModal(
      'Sending Messages',
      'You are about to send the message on behalf of ' +
        this.app.account.name +
        '. Switch to a different account if this is not intended.'
    );

    this.utils
      .showComponentDialog(
        MessageEditComponent,
        {
          preSelectUserIds: accountId
            ? [accountId]
            : this.dataSource.selection.selected
        },
        {
          width: '1200px'
        }
      )
      .then((response) => {
        if (response) {
          this.utils.showToast(
            'You can view the message stats in the messages section.'
          );
        }
      });
  }

  /**
   * Returns the tooltip text based on the given account settings record.
   *
   * @param {AccountModel} record - The account settings record.
   * @returns {string} - The tooltip text.
   */
  getSettingsTooltip(record: AccountModel): string {
    const tooltips: string[] = [];
    const createTooltip = (condition: boolean, message: string) =>
      condition ? `${message} is enabled.` : `${message} is disabled.`;

    tooltips.push(
      createTooltip(record.allow_auto_site_signout, 'Auto sign-outs')
    );
    tooltips.push(
      createTooltip(
        record.enable_site_sign_in_ack_reminders,
        'Risk acknowledgement reminders at sign-in'
      )
    );
    tooltips.push(
      createTooltip(record.enable_sms_notifications, 'SMS messaging')
    );
    tooltips.push(
      createTooltip(record.hazard_verification, 'Hazards verification')
    );
    tooltips.push(
      createTooltip(
        record.include_in_search_results,
        'Include in search results'
      )
    );
    tooltips.push(
      createTooltip(
        record.induction_notifications_enabled,
        'Induction notifications'
      )
    );
    tooltips.push(createTooltip(record.is_logbook, 'Digital Logbook'));
    tooltips.push(createTooltip(record.pandemic_enabled, 'Covid-19 module'));
    tooltips.push(
      createTooltip(
        record.site_arrival_notifications_enabled,
        'Sign-in arrival notifications'
      )
    );
    tooltips.push(
      createTooltip(
        record.include_main_contact_in_notifications,
        'Including the main account contact in all notifications'
      )
    );
    tooltips.push(
      createTooltip(
        record.enable_manual_gps_signins,
        'Enable this option to allow users to manually sign in using the ‘GPS Sign-in’ button in the mobile app'
      )
    );

    return '- ' + tooltips.join('\n- ');
  }

  /**
   * Open the list filters.
   */
  onOpenFilters() {
    this.utils
      .showComponentDialog(
        AccountsFilterComponent,
        {
          subscription: this.dataSource.subscription,
          account_type: this.dataSource.account_type,
          sms_notifications: this.dataSource.sms_notifications,
          associated_users: this.dataSource.associated_users,
          include_merged_accounts: this.dataSource.include_merged_accounts,
          include_personal_accounts: this.dataSource.include_personal_accounts
        },
        {
          width: '400px'
        }
      )
      .then((response) => {
        if (response) {
          this.dataSource.subscription = response.subscription;
          this.dataSource.account_type = response.account_type;
          this.dataSource.sms_notifications = response.sms_notifications;
          this.dataSource.associated_users = response.associated_users;
          this.dataSource.include_merged_accounts =
            response.include_merged_accounts;
          this.dataSource.include_personal_accounts =
            response.include_personal_accounts;
          this.saveFilters();
          this.dataSource.getData(true);
        }
      });
  }

  /**
   * Quick access into a client account as an administrator.
   * Similar code in sc-web-portal/src/app/shared/auth.service.ts
   * Similar code in sc-web-portal/src/app/top-nav/top-nav.component.ts
   * Similar code in sc-web-portal/src/app/user/account-switcher/account-switcher.component.ts
   */
  onQuickAccess(accountId: number): void {
    this.api
      .makeRequest('post', 'v2/admin/accounts/' + accountId + '/quick-access')
      .then(
        (response: any): void => {
          this.storage.set('AuthToken', response.data.api_token);
          this.storage.set('Permissions', response.data.permissions);

          this.app.user.apply(response.data.user);
          this.app.account.apply(response.data.account);
          this.app.userAccountLink.apply(response.data.user_account_link);

          // Apply permissions last: userCanAccess() also reads app.user.is_sitesoft_admin, and applying
          // permissions emits the change notification that components use to recompute their flags.
          this.rbac.applyPermissions(response.data.permissions);

          this.sentry.setActiveAccount(this.app.account.id);

          this.appcuesService.onIdentifyGroup(this.app.account.name, {
            name: this.app.account.name,
            industry: this.app.account.industry,
            address: this.app.account.address,
            suburb: this.app.account.suburb,
            city: this.app.account.city,
            postal_code: this.app.account.postal_code,
            country: this.app.account.country
          });

          this.app.quickAccessSession = response.data.quick_access_session;

          this.router.navigate(['/dashboard']);

          this.utils.showToast(
            `You now have quick access to ${this.app.account.name}.`
          );
        }
      )
      .catch(
        (): void => {
          this.utils.showToast(
            'Failed to quick access the account. Please try again.'
          );
        }
      );
  }
}

export class AccountsDataSource extends ApiDataSource {
  order_by = 'id';
  order = 'asc';

  subscription: string = '';
  account_type: string = '';
  sms_notifications: string = '';
  associated_users: { id: number; name: string }[] = [];
  include_merged_accounts: boolean = false;
  // Personal accounts are hidden by default (SC-301). They carry the owner's own name, so leaving them
  // in would clutter this list with entries that look like people posing as companies.
  include_personal_accounts: boolean = false;

  getData(resetOffset: boolean = false) {
    const params: any = {
      subscription: this.subscription,
      include_merged_accounts: this.include_merged_accounts,
      include_personal_accounts: this.include_personal_accounts
    };
    if (this.account_type) {
      params.account_type = this.account_type;
    }
    if (this.sms_notifications) {
      params.sms_notifications = this.sms_notifications;
    }
    if (this.associated_users.length) {
      params.associated_user_ids = this.associated_users
        .map((u) => u.id)
        .join(',');
    }
    this.makeRequest('v2/admin/accounts', resetOffset, params);
  }
}
